import React from "react";
import cx from "classnames";
import { getUpns } from "@modules/datasheets/services/datasheet";
import {
  generateLetterFromIndividual,
  openTestResultsFromIndividual,
  orderTestFromIndividual,
} from "@common/actions/individuals";

import { Tabs, TabsItemOnOrderChange, TabsProps } from "../tabs";
import { Header } from "./components/header";
import { ResultProps, useDatasheetResultMainController } from "./controllers/main";
import { MessageBox } from "@common/components/MessageBox";
import { useMessage } from "@common/hooks/message";
import { trDts } from "@modules/datasheets/utils/i18n";
import { Datasheet } from "./components/datasheet";
import { DatasheetPrintingSettings } from "../datasheet-printing-settings";
import FormatManagement from "@common/components/FormatManagement";
import { getDatasheetFolders } from "@modules/datasheets/services/folders";
import { getTdsResultFolderFormats } from "@modules/datasheets/services/formats";

import './result.styles.scss';
import { DatasheetResultInteractionLockedContext } from './components/datasheet/context';

const TdsFormatPicker: React.FC<{
  tableFieldId: number;
  context: number;
  onClose: () => void;
  onSubmit: (format: { id: number; name: string }, folder: any) => void;
}> = ({ tableFieldId, context, onClose, onSubmit }) => {
  const [folders, setFolders] = React.useState<any[]>([]);
  const [formats, setFormats] = React.useState<any[]>([]);

  const getFolders = React.useCallback(async () => {
    const result = await getDatasheetFolders(context, true);
    setFolders(result);
  }, [context]);

  const getFormats = React.useCallback(async (folderId: number) => {
    const result = await getTdsResultFolderFormats({ folderId, context, tableId: tableFieldId });
    setFormats(result);
  }, [tableFieldId, context]);

  return (
    <FormatManagement
      title={trDts("Load Format")}
      folders={folders}
      formats={formats}
      getFolders={getFolders}
      getFormats={getFormats}
      resetFormats={() => setFormats([])}
      close={onClose}
      onSubmit={onSubmit}
      submitBtnProps={{ text: trDts("Load") }}
      isAllowedToSubmit={({ formatId }: { formatId?: number | null }) => Boolean(formatId)}
    />
  );
};

export const Result: React.FC<ResultProps> = (props) => {
  const [splitViewDisabled, setSplitViewDisabled] = React.useState(() => {
    if (!props.formatOnly || !props.groupKey) return false;
    const states = (window as any).__REACT_DS_SPLIT_STATES;
    return states ? !!states[props.groupKey] : false;
  });

  React.useEffect(() => {
    if (!props.formatOnly || !props.groupKey) return;
    const currentStates = (window as any).__REACT_DS_SPLIT_STATES;
    if (currentStates && props.groupKey in currentStates) {
      setSplitViewDisabled(!!currentStates[props.groupKey]);
    }
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && detail.groupKey === props.groupKey) {
        setSplitViewDisabled(!!detail.disabled);
      }
    };
    window.addEventListener('ds-split-disabled-change', handler);
    return () => window.removeEventListener('ds-split-disabled-change', handler);
  }, [props.formatOnly, props.groupKey]);

  const {
    handleDatasheetElementsRedo,
    handleDatasheetElementsUndo,
    canRedo,
    canUndo,
    datasheet,
    isLoadingData,
    isTableDatasheetResult,
    tdsCurrentRowLabel,
    tdsCanMovePrev,
    tdsCanMoveNext,
    handleTdsFirstRow,
    handleTdsPrevRow,
    handleTdsNextRow,
    handleTdsLastRow,
    handleTdsAddRow,
    currentTabId,
    handleSelectedTabChange,
    handleTabOrderChange,
    handleUpnChange,
    currentUpn,
    datasheetElementsProcessed,
    pageBreaks,
    gridSettingsMargin,
    updateDatasheetElement,
    handlePrintDatasheetElements,
    handlePrintingSettingsClose,
    printingSettingsOpened,
    calculatePaperCount,
    handlePrintWithPageSkip,
    printableDatasheetHolderId,
    getDatasheetFolderFormats,
    getFoldersForDatasheet,
    handleDatasheetFormatSelect,
    handleTdsFormatSelect,
    showNoDefaultFormatDialog,
    setShowNoDefaultFormatDialog,
    validationMessageProps,
    closeValidationMessage,
    fieldValidationState,
    datasheetEditLocked,
    datasheetLockBlocked,
    datasheetLockUserName,
  } = useDatasheetResultMainController(props);

  const { closeMessage, showConfirmMessage, messageProps } = useMessage();
  const [forceOpenFormatPicker, setForceOpenFormatPicker] = React.useState(false);
  const [tdsFormatPickerOpened, setTdsFormatPickerOpened] = React.useState(false);

  const handleSetFolderDefaultFormat = React.useCallback(
    async (params: { folderId: number; formatId: number }) => {
      // Only supported via the backend API when context is provided
      if (!props.context) {
        return;
      }

      // Use the rootFolderId (IDS/PDS folder where the individual/pedigree
      // belongs) when available; fall back to the folderId from the selector
      // for backwards compatibility with older integrations.
      const targetFolderId = props.rootFolderId ?? params.folderId;

      try {
        const { setDatasheetDefaultFormat } = await import("@modules/datasheets/services/datasheet");
        await setDatasheetDefaultFormat({
          context: props.context,
          id: targetFolderId,
          formatId: params.formatId,
          folder: true,
        });
      } catch (err) {
        // Do not block UI if persisting the default fails
        // eslint-disable-next-line no-console
        console.error("Failed to save folder default datasheet format", err);
      }
    },
    [props.context, props.rootFolderId]
  );

  const handleSetPedigreeDefaultFormat = React.useCallback(
    async (formatId: number) => {
      // For IDS (context=1) and PDS (context=2), save default at the object (individual/pedigree) level
      if (!props.context || !props.globalId) {
        return;
      }
      try {
        const { setDatasheetDefaultFormat } = await import("@modules/datasheets/services/datasheet");
        await setDatasheetDefaultFormat({
          context: props.context,
          id: props.globalId,
          formatId,
          folder: false,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to save datasheet default format", err);
      }
    },
    [props.context, props.globalId]
  );

  const hasIndividualContext = props.context === 1 && props.globalId != null;

  const handleGenerateLetter = React.useCallback(() => {
    if (hasIndividualContext && props.globalId != null) {
      generateLetterFromIndividual(props.globalId);
    }
  }, [hasIndividualContext, props.globalId]);

  const handleOpenTestResults = React.useCallback(() => {
    if (hasIndividualContext && props.globalId != null) {
      openTestResultsFromIndividual(props.globalId);
    }
  }, [hasIndividualContext, props.globalId]);

  const handleOrderTest = React.useCallback(() => {
    if (hasIndividualContext && props.globalId != null) {
      orderTestFromIndividual(props.globalId);
    }
  }, [hasIndividualContext, props.globalId]);


  // Table datasheet result: show fixed format only, no format chooser (same as GWT open table datasheet from result).
  const showFormatChooser = !isTableDatasheetResult;

  const showInitialLoader = !isTableDatasheetResult && (datasheet.id === 0 || !datasheet.tabs?.length);

  return (
    <div
      className={cx("DatasheetsResult", {
        "DatasheetsResult--formatOnly": props.formatOnly,
        "DatasheetsResult--tableDatasheet": isTableDatasheetResult,
      })}
    >
      <MessageBox hideMessageBox={closeMessage} {...messageProps} />
      <MessageBox hideMessageBox={closeValidationMessage} {...validationMessageProps} />
      {datasheetLockBlocked && (
        <div className="DatasheetsResult__lockBanner" role="status">
          {datasheetLockUserName && datasheetLockUserName.trim()
            ? trDts("datasheetLockedByOtherUserNamed").replace("{name}", datasheetLockUserName.trim())
            : trDts("datasheetLockedByOtherUser")}
        </div>
      )}
      {showNoDefaultFormatDialog && (
        <MessageBox
          hideMessageBox={closeMessage}
          show={true}
          size="small"
          header={trDts("confirmationMessage")}
          text="There is no default format defined for this datasheet. Please contact your administrator to apply one. Do you wish to select a temporary format?"
          confirmText="Yes"
          cancelText="No"
          onCustomOk={() => {
            setShowNoDefaultFormatDialog(false);
            if (isTableDatasheetResult) {
              setTdsFormatPickerOpened(true);
            } else {
              setForceOpenFormatPicker(true);
            }
          }}
          onCustomCancel={() => {
            setShowNoDefaultFormatDialog(false);
            setForceOpenFormatPicker(false);
          }}
        />
      )}
      {tdsFormatPickerOpened && props.tableFieldId && (
        <TdsFormatPicker
          tableFieldId={props.tableFieldId}
          context={props.tableContext ?? props.context ?? 1}
          onClose={() => setTdsFormatPickerOpened(false)}
          onSubmit={(format) => {
            handleTdsFormatSelect({ format });
            setTdsFormatPickerOpened(false);
          }}
        />
      )}
      {isTableDatasheetResult ? (
        <div className="DatasheetsResult__header DatasheetsResult__tdsToolbar">
          <button type="button" className="DatasheetsResult__tdsToolbarBtn" onClick={handleTdsFirstRow} disabled={!tdsCanMovePrev} aria-label="First row">
            <img src="gif/arrow_first_rec.gif" alt="" />
          </button>
          <button type="button" className="DatasheetsResult__tdsToolbarBtn" onClick={handleTdsPrevRow} disabled={!tdsCanMovePrev} aria-label="Previous row">
            <img src="gif/arrow_left2.gif" alt="" />
          </button>
          <input type="text" className="DatasheetsResult__tdsToolbarStatus" value={tdsCurrentRowLabel} readOnly aria-label="Current row" />
          <button type="button" className="DatasheetsResult__tdsToolbarBtn" onClick={handleTdsNextRow} disabled={!tdsCanMoveNext} aria-label="Next row">
            <img src="gif/arrow_right2.gif" alt="" />
          </button>
          <button type="button" className="DatasheetsResult__tdsToolbarBtn" onClick={handleTdsLastRow} disabled={!tdsCanMoveNext} aria-label="Last row">
            <img src="gif/arrow_last_rec.gif" alt="" />
          </button>
          <button
            type="button"
            className="Button btn btn-primary DatasheetsResult__tdsToolbarAddBtn"
            onClick={handleTdsAddRow}
          >
            {trDts("New Table Row")}
          </button>
        </div>
      ) : (
      <Header
        disabledState={{
          print: !datasheet.tabs.length || datasheetEditLocked,
          undo: !canUndo || datasheetEditLocked,
          redo: !canRedo || datasheetEditLocked,
          orderTest: !hasIndividualContext || datasheetEditLocked,
          generateLetter: !hasIndividualContext || datasheetEditLocked,
          testResults: !hasIndividualContext || datasheetEditLocked,
        }}
        // When opened from pedigree split view, show only the format picker in the header.
        // Table datasheet result: no format picker – format is fixed by the table (same as GWT).
        formatOnly={props.formatOnly}
        showFormatPicker={showFormatChooser}
        forceOpenFormatPicker={forceOpenFormatPicker}
        onFormatPickerClose={() => setForceOpenFormatPicker(false)}
        className="DatasheetsResult__header"
        upn={currentUpn}
        onUpnChange={handleUpnChange}
        getUpns={getUpns}
        onPrint={handlePrintDatasheetElements}
        onUndo={handleDatasheetElementsUndo}
        onRedo={handleDatasheetElementsRedo}
        onOrderTest={handleOrderTest}
        onGenerateLetter={handleGenerateLetter}
        onOrderTestResults={handleOpenTestResults}
        getDatasheetFolderFormats={getDatasheetFolderFormats}
        getFoldersForDatasheet={getFoldersForDatasheet}
        onDatasheetFormatsSubmit={handleDatasheetFormatSelect}
        onSetFolderDefaultFormat={handleSetFolderDefaultFormat}
        onSetPedigreeDefaultFormat={handleSetPedigreeDefaultFormat}
        canSetFolderDefault={props.canSetFolderDefault}
        context={props.context}
        templateOptionOn={props.templateOptionOn}
        initialSelectedFormat={datasheet.id && datasheet.name ? { id: datasheet.id, name: datasheet.name, folderId: 0 } : undefined}
        reloadRecentlyUsedKey={props.reloadRecentlyUsedKey}
      />
      )}
      <div className="DatasheetsResult__content">
        <Tabs
          items={datasheet.tabs}
          value={currentTabId ?? ""}
          onChange={handleSelectedTabChange as TabsProps["onChange"]}
          classes={{
            tabs: "DatasheetsResult__content__tabs",
          }}
          onTabOrderChange={handleTabOrderChange as TabsItemOnOrderChange}
          formatManagement={showFormatChooser ? {
            getFoldersForDatasheet,
            getDatasheetFolderFormats,
            onDatasheetFormatsSubmit: handleDatasheetFormatSelect,
            forceOpenFormatPicker,
            onFormatPickerClose: () => setForceOpenFormatPicker(false),
            onSetFolderDefaultFormat: handleSetFolderDefaultFormat,
            onSetPedigreeDefaultFormat: handleSetPedigreeDefaultFormat,
            canSetFolderDefault: props.canSetFolderDefault,
            context: props.context,
            templateOptionOn: props.templateOptionOn,
            rootFolderId: props.rootFolderId,
            formatOnly: props.formatOnly,
            initialSelectedFormat: datasheet.id && datasheet.name ? { id: datasheet.id, name: datasheet.name, folderId: 0 } : undefined,
            reloadRecentlyUsedKey: props.reloadRecentlyUsedKey,
            interactionLocked: datasheetEditLocked,
          }: undefined}
        />
        {printingSettingsOpened && (
          <DatasheetPrintingSettings
            onClose={handlePrintingSettingsClose}
            calculatePaperCount={calculatePaperCount}
            onSubmit={handlePrintWithPageSkip}
          />
        )}
        <div className="DatasheetsResult__content__datasheetWrapper" style={{ position: "relative", flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
          {showInitialLoader && (
            <div className="DatasheetsResult__loading" aria-busy="true" aria-live="polite">
              <div className="DatasheetsResult__loading__spinner">
                <i className="fa fa-spinner fa-spin" aria-hidden />
              </div>
              <div className="DatasheetsResult__loading__text">Loading...</div>
            </div>
          )}
          {!showInitialLoader && isLoadingData && (
            <div className="DatasheetsResult__loading" aria-busy="true" aria-live="polite">
              <div className="DatasheetsResult__loading__spinner">
                <i className="fa fa-spinner fa-spin" aria-hidden />
              </div>
              <div className="DatasheetsResult__loading__text">Loading...</div>
            </div>
          )}
          <DatasheetResultInteractionLockedContext.Provider value={datasheetEditLocked}>
          <Datasheet
          printableDatasheetHolderId={printableDatasheetHolderId}
          pageBreaks={pageBreaks}
          gridSettingsMargin={gridSettingsMargin}
          datasheet={datasheet}
          datasheetElements={datasheetElementsProcessed}
          onDatasheetElementUpdate={updateDatasheetElement}
          className="DatasheetsResult__content__datasheet"
          context={props.context}
          globalId={props.globalId}
          parentFormatId={props.formatId ?? datasheet.id ?? 0}
          parentTabId={currentTabId != null ? Number(currentTabId) : undefined}
          formatOnly={props.formatOnly}
          fieldValidation={fieldValidationState}
          splitViewDisabled={splitViewDisabled}
        />
          </DatasheetResultInteractionLockedContext.Provider>
        </div>
      </div>
    </div>
  );
};